import { StatsCollectors, StatsToLog } from '../metrics';
import { type SessionStatsProvider } from './types';

export class MetricsMonitor {
  private intervalId: NodeJS.Timeout | undefined;
  public static readonly interval = 3000;

  constructor(
    private readonly statsProvider: SessionStatsProvider,
    private readonly stats: StatsCollectors,
  ) {}

  start(): void {
    clearInterval(this.intervalId);

    this.intervalId = setInterval(() => {
      void this.addStats();
    }, MetricsMonitor.interval);
  }

  stop(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  reset(): void {
    for (const value of Object.values(this.stats)) {
      value.reset();
    }
  }

  getMetrics(): StatsToLog {
    const cpu = this.stats.metricCpu.getAggregated();

    const videoMosInbound = this.stats.metricInboundVideoMos.getAggregated();
    const audioMosInbound = this.stats.metricInboundAudioMos.getAggregated();

    const networkMosInbound =
      this.stats.metricInboundNetworkMos.getAggregated();
    const networkQualityRatioInbound =
      this.stats.metricInboundNetworkQualityRatio.getAggregated();

    const networkMosOutbound =
      this.stats.metricOutboundNetworkMos.getAggregated();
    const networkQualityRatioOutbound =
      this.stats.metricOutboundNetworkQualityRatio.getAggregated();

    const frameworkMosInbound =
      this.stats.metricInboundFrameRate.getAggregated();
    const frameworkMosOutbound =
      this.stats.metricOutboundFrameRate.getAggregated();

    const inboundResolution =
      this.stats.metricInboundResolution.getAggregated();
    const outboundResolution =
      this.stats.metricOutboundResolution.getAggregated();

    const inboundBitrate = this.stats.metricInboundBitrate.getAggregated();
    const outboundBitrate = this.stats.metricOutboundBitrate.getAggregated();
    const inboundVideoBitrate =
      this.stats.metricInboundVideoBitrate.getAggregated();
    const outboundVideoBitrate =
      this.stats.metricOutboundVideoBitrate.getAggregated();
    const inboundAudioBitrate =
      this.stats.metricInboundAudioBitrate.getAggregated();
    const outboundAudioBitrate =
      this.stats.metricOutboundAudioBitrate.getAggregated();

    const stats: StatsToLog = {
      // How long was CPU in each state. The total of all state below adds up to 1.
      // Multiply each value by 100 to get is a % if needed.
      metricCpuNominal: cpu['nominal'],
      metricCpuFair: cpu['fair'],
      metricCpuCritical: cpu['critical'],
      metricCpuSerious: cpu['serious'],

      // The Vonage MOS score. We get an MOS per subscriber so this is based of the values of all subscribers
      metricInboundVideoMosAvg: videoMosInbound.average,
      metricInboundVideoMosMin: videoMosInbound.min,
      metricInboundVideoMosMax: videoMosInbound.max,

      // The Vonage MOS score. We get an MOS per subscriber so this is based of the values of all subscribers
      metricInboundAudioMosAvg: audioMosInbound.average,
      metricInboundAudioMosMin: audioMosInbound.min,
      metricInboundAudioMosMax: audioMosInbound.max,

      // The inbound network MOS score. Collected in our qualityOfServicePlugin from 3rd party
      // https://www.npmjs.com/package/webrtc-issue-detector lib
      metricInboundNetworkMosAvg: networkMosInbound.average,
      metricInboundNetworkMosMin: networkMosInbound.min,
      metricInboundNetworkMosMax: networkMosInbound.max,

      // The computed NPI based on the inbound MOS score above.
      metricInboundNetworkQualityRatioHigh: networkQualityRatioInbound['high'],
      metricInboundNetworkQualityRatioMedium:
        networkQualityRatioInbound['medium'],
      metricInboundNetworkQualityRatioLow: networkQualityRatioInbound['low'],

      // The outbound network MOS score. Collected in our qualityOfServicePlugin from 3rd party
      // https://www.npmjs.com/package/webrtc-issue-detector lib
      metricOutboundNetworkMosAvg: networkMosOutbound.average,
      metricOutboundNetworkMosMin: networkMosOutbound.min,
      metricOutboundNetworkMosMax: networkMosOutbound.max,

      // The computed NPI based on the outbound MOS score above.
      metricOutboundNetworkQualityRatioHigh:
        networkQualityRatioOutbound['high'],
      metricOutboundNetworkQualityRatioMedium:
        networkQualityRatioOutbound['medium'],
      metricOutboundNetworkQualityRatioLow: networkQualityRatioOutbound['low'],

      // The Vonage frame rate from the web rtc stats report.
      // We get this per subscriber so this is based of the values of all subscribers
      metricInboundFrameRateAvg: frameworkMosInbound.average,
      metricInboundFrameRateMin: frameworkMosInbound.min,
      metricInboundFrameRateMax: frameworkMosInbound.max,

      // The Vonage frame rate from the web rtc stats report.
      // We get this from the publisher
      metricOutboundFrameRateAvg: frameworkMosOutbound.average,
      metricOutboundFrameRateMin: frameworkMosOutbound.min,
      metricOutboundFrameRateMax: frameworkMosOutbound.max,

      // The Vonage resolution from the web rtc stats report.
      // We get this per subscriber so this is based of the values of all subscribers
      metricInboundResolution1080p: inboundResolution['1080p'],
      metricInboundResolution720p: inboundResolution['720p'],
      metricInboundResolution480p: inboundResolution['480p'],
      metricInboundResolution360p: inboundResolution['360p'],
      metricInboundResolution240p: inboundResolution['240p'],
      metricInboundResolution180p: inboundResolution['180p'],
      metricInboundResolution144p: inboundResolution['144p'],

      metricInboundResolutionMostUsed:
        this.stats.metricInboundResolutionMostUsed.getAggregated(),

      metricInboundResolutionChangeCount:
        this.stats.metricInboundResolutionChangeCount.getAggregated(),

      // The Vonage resolution from the web rtc stats report.
      // We get this from the publisher
      metricOutboundResolution1080p: outboundResolution['1080p'],
      metricOutboundResolution720p: outboundResolution['720p'],
      metricOutboundResolution480p: outboundResolution['480p'],
      metricOutboundResolution360p: outboundResolution['360p'],
      metricOutboundResolution240p: outboundResolution['240p'],
      metricOutboundResolution180p: outboundResolution['180p'],
      metricOutboundResolution144p: outboundResolution['144p'],

      metricOutboundResolutionMostUsed:
        this.stats.metricOutboundResolutionMostUsed.getAggregated(),

      metricOutboundResolutionChangeCount:
        this.stats.metricOutboundResolutionChangeCount.getAggregated(),

      // Bitrate metrics
      metricInboundBitrateAvg: inboundBitrate.average,
      metricInboundBitrateMin: inboundBitrate.min,
      metricInboundBitrateMax: inboundBitrate.max,

      metricOutboundBitrateAvg: outboundBitrate.average,
      metricOutboundBitrateMin: outboundBitrate.min,
      metricOutboundBitrateMax: outboundBitrate.max,

      metricInboundVideoBitrateAvg: inboundVideoBitrate.average,
      metricInboundVideoBitrateMin: inboundVideoBitrate.min,
      metricInboundVideoBitrateMax: inboundVideoBitrate.max,

      metricOutboundVideoBitrateAvg: outboundVideoBitrate.average,
      metricOutboundVideoBitrateMin: outboundVideoBitrate.min,
      metricOutboundVideoBitrateMax: outboundVideoBitrate.max,

      metricInboundAudioBitrateAvg: inboundAudioBitrate.average,
      metricInboundAudioBitrateMin: inboundAudioBitrate.min,
      metricInboundAudioBitrateMax: inboundAudioBitrate.max,

      metricOutboundAudioBitrateAvg: outboundAudioBitrate.average,
      metricOutboundAudioBitrateMin: outboundAudioBitrate.min,
      metricOutboundAudioBitrateMax: outboundAudioBitrate.max,
    };

    return stats;
  }

  private async addStats(): Promise<void> {
    await this.statsProvider.collectStats();
  }
}
